import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ContractDetailRecord, ContractStatus } from '@shared/types';
import { subscribeToShellDeleteRequests } from '@renderer/app/events/shell-delete-events';
import { useShellStore } from '@renderer/app/state/shell-store';
import { EditorBodyLayout, EditorHeroCard, EditorPageLayout, EditorSectionCard } from '@renderer/shared/components/editor-scaffold';
import { Icon } from '@renderer/shared/components/icons';
import {
  EditorTabs,
  EmptyState,
  OverflowMenuButton,
  SelectField,
  StatusBadge,
  TextAreaField,
  TextField,
  ValuePair
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { useRegisterEditorSaveHandler } from '@renderer/shared/hooks/use-register-editor-save-handler';
import { areDraftsEqual } from '@renderer/shared/utils/draft-equality';
import { deleteContracts, getContractById, updateContract } from './contracts-repository';
import { selectContractEditorModel } from './contracts-selectors';
import { useContractsViewStore } from './contracts-view-store';

function formatSnapshotAddress(value: ContractDetailRecord['contract']['customerSnapshot'], fallback: string): string {
  return value.billingAddress?.formatted ?? fallback;
}

function formatSnapshotRepresentative(value: ContractDetailRecord['contract']['customerSnapshot'], fallback: string): string {
  if (!value.representative.name) {
    return fallback;
  }

  if (!value.representative.role) {
    return value.representative.name;
  }

  return `${value.representative.name} - ${value.representative.role}`;
}

interface ContractDraft {
  title: string;
  status: ContractStatus;
  effectiveFrom: string;
  effectiveUntil: string;
  totalValue: string;
  currency: string;
  serviceScopeSummary: string;
  renewalNotes: string;
  terminationNotes: string;
  paymentNotes: string;
  scheduleSummary: string;
}

function formatDateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function toIsoDate(value: string): string | null {
  return value ? `${value}T12:00:00.000Z` : null;
}

function toDraft(detail: ContractDetailRecord): ContractDraft {
  return {
    title: detail.contract.title,
    status: detail.contract.status,
    effectiveFrom: formatDateInputValue(detail.contract.effectiveFrom),
    effectiveUntil: formatDateInputValue(detail.contract.effectiveUntil),
    totalValue: detail.contract.totalValue === null ? '' : String(detail.contract.totalValue),
    currency: detail.contract.currency,
    serviceScopeSummary: detail.contract.serviceScopeSummary ?? '',
    renewalNotes: detail.contract.renewalNotes ?? '',
    terminationNotes: detail.contract.terminationNotes ?? '',
    paymentNotes: detail.contract.paymentNotes ?? '',
    scheduleSummary: detail.contract.scheduleSummary ?? ''
  };
}

export function ContractEditorScreen(): JSX.Element {
  const navigate = useNavigate();
  const { contractId } = useParams();
  const { locale, t } = useI18n();
  const setRouteDetailTitle = useShellStore((state) => state.setRouteDetailTitle);
  const { editorTab, setEditorTab } = useContractsViewStore();
  const [detail, setDetail] = useState<ContractDetailRecord | null>(null);
  const [draft, setDraft] = useState<ContractDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const savedDraft = useMemo(() => (detail ? toDraft(detail) : null), [detail]);

  useEffect(() => {
    if (!contractId) {
      setError('Missing contract id.');
      return;
    }

    let active = true;
    void getContractById(contractId)
      .then((nextDetail) => {
        if (!active) {
          return;
        }

        if (!nextDetail) {
          setError('Contract not found.');
          return;
        }

        setDetail(nextDetail);
        setDraft(toDraft(nextDetail));
        setRouteDetailTitle(nextDetail.contract.title);
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Contract unavailable.');
        }
      });

    return () => {
      active = false;
      setRouteDetailTitle(null);
    };
  }, [contractId, setRouteDetailTitle]);

  useEffect(
    () =>
      subscribeToShellDeleteRequests((detailRequest) => {
        if (detailRequest.domain !== 'contracts' || !detail || !detailRequest.ids.includes(detail.contract.id)) {
          return;
        }

        void deleteContracts({ ids: [detail.contract.id] }).then(() => navigate('/contracts'));
      }),
    [detail, navigate]
  );

  useRegisterEditorSaveHandler(
    detail && draft && savedDraft
      ? () => {
          if (isSaving) {
            return;
          }

          const nextIsDirty = !areDraftsEqual(draft, savedDraft);
          if (!nextIsDirty) {
            return;
          }

          void handleSave();
        }
      : null
  );

  if (error) {
    return (
      <div className="av-page">
        <EmptyState title={t('contracts.title')} description={error} />
      </div>
    );
  }

  if (!detail) {
    return <div className="av-page" />;
  }

  const model = selectContractEditorModel(detail, { locale, t });
  const contractIdToDelete = detail.contract.id;
  const isDirty = draft && savedDraft ? !areDraftsEqual(draft, savedDraft) : false;
  const statusOptions = (['draft', 'pending_review', 'ready_to_send', 'sent', 'signed', 'expired', 'archived'] satisfies ContractStatus[]).map(
    (value) => ({
      value,
      label: t(`enums.contractStatus.${value}`)
    })
  );

  async function handleDeleteContract(): Promise<void> {
    if (!window.confirm(t('common.confirmDeleteSoft'))) {
      return;
    }

    await deleteContracts({ ids: [contractIdToDelete] });
    navigate('/contracts');
  }

  async function handleSave(): Promise<void> {
    if (!draft || !detail) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const totalValue = draft.totalValue.trim() ? Number(draft.totalValue) : null;
      if (totalValue !== null && Number.isNaN(totalValue)) {
        throw new Error('Invalid total value.');
      }

      const detailResponse = await updateContract({
        id: detail.contract.id,
        title: draft.title.trim(),
        status: draft.status,
        effectiveFrom: toIsoDate(draft.effectiveFrom),
        effectiveUntil: toIsoDate(draft.effectiveUntil),
        totalValue,
        currency: draft.currency.trim() || detail.contract.currency,
        serviceScopeSummary: draft.serviceScopeSummary,
        renewalNotes: draft.renewalNotes.trim() ? draft.renewalNotes : null,
        terminationNotes: draft.terminationNotes.trim() ? draft.terminationNotes : null,
        paymentNotes: draft.paymentNotes.trim() ? draft.paymentNotes : null,
        scheduleSummary: draft.scheduleSummary.trim() ? draft.scheduleSummary : null
      });

      setDetail(detailResponse);
      setDraft(toDraft(detailResponse));
      setRouteDetailTitle(detailResponse.contract.title);
    } catch (nextError) {
      setSaveError(nextError instanceof Error ? nextError.message : 'Save failed.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel(): void {
    if (!detail) {
      return;
    }

    setSaveError(null);
    setDraft(toDraft(detail));
  }

  return (
    <EditorPageLayout
      hero={
        <EditorHeroCard
          actions={
            <>
              <button className="av-secondary-button" type="button" onClick={() => void handleDeleteContract()}>
                <Icon name="trash" />
                <span>{t('shell.delete')}</span>
              </button>
              <OverflowMenuButton />
            </>
          }
        >
          <div className="av-editor-hero-card__content">
            <div className="av-editor-hero-card__identity">
              <div className="av-editor-hero-card__icon">
                <Icon name="contracts" />
              </div>
              <div className="av-editor-hero-card__identity-copy">
                <h2>{model.title}</h2>
                <div className="av-editor-hero-card__meta">
                  <StatusBadge label={model.contractNumber} tone="dark" />
                  <StatusBadge label={model.statusLabel} tone={model.statusTone} />
                </div>
              </div>
            </div>

            <div className="av-editor-hero-card__endpoints">
              <div className="av-endpoint-row">
                <Icon name="customers" />
                <span>{model.customerName}</span>
              </div>
              <div className="av-endpoint-row">
                <Icon name="work" />
                <span>{model.projectName}</span>
              </div>
              <div className="av-endpoint-row">
                <Icon name="document" />
                <span>{model.issuerName}</span>
              </div>
            </div>
          </div>
        </EditorHeroCard>
      }
      heroAside={
        <EditorSectionCard title={t('contracts.sections.summary')} actions={<OverflowMenuButton />}>
          <div className="av-summary-stack">
            <ValuePair label={t('contracts.summary.effectiveFrom')} value={model.effectiveFromLabel} />
            <ValuePair label={t('contracts.summary.effectiveUntil')} value={model.effectiveUntilLabel} />
            <ValuePair label={t('contracts.summary.value')} value={model.totalValueLabel} />
            <ValuePair label={t('contracts.summary.signature')} value={model.signatureLabel} />
          </div>
        </EditorSectionCard>
      }
      tabs={
        <EditorTabs
          tabs={[
            { id: 'general', label: t('contracts.tabs.general'), icon: 'contracts' },
            { id: 'document', label: t('contracts.tabs.document'), icon: 'document' },
            { id: 'activity', label: t('contracts.tabs.activity'), icon: 'timer' }
          ]}
          value={editorTab}
          onChange={setEditorTab}
        />
      }
    >
      <EditorBodyLayout
        main={
          <>
            {saveError ? <div className="av-inline-error">{saveError}</div> : null}
            {editorTab === 'general' ? (
              <>
                <EditorSectionCard
                  title={t('contracts.sections.general')}
                  actions={
                    <div className="av-inline-metadata">
                      <button className="av-secondary-button" type="button" onClick={handleCancel} disabled={!isDirty || isSaving}>
                        {t('common.cancel')}
                      </button>
                      <button className="av-primary-button" type="button" onClick={() => void handleSave()} disabled={!isDirty || isSaving}>
                        {t('common.applyChanges')}
                      </button>
                    </div>
                  }
                >
                  <div className="av-form-grid">
                    <label className="av-form-field">
                      <span>{t('contracts.fields.title')}</span>
                      <TextField
                        value={draft?.title ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, title: value } : current))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('contracts.fields.contractNumber')}</span>
                      <input className="av-input" readOnly type="text" value={detail.contract.contractNumber} />
                    </label>
                    <label className="av-form-field">
                      <span>{t('contracts.fields.status')}</span>
                      <SelectField
                        value={draft?.status ?? detail.contract.status}
                        options={statusOptions}
                        onChange={(event) =>
                          setDraft((current) => (current ? { ...current, status: event.target.value as ContractStatus } : current))
                        }
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('contracts.fields.template')}</span>
                      <input className="av-input" readOnly type="text" value={model.templateLabel} />
                    </label>
                    <div className="av-form-field">
                      <span>{t('contracts.fields.customer')}</span>
                      <Link className="av-inline-link av-inline-link--field" to={`/customers/${detail.contract.customerId}`}>
                        <span>{model.customerName}</span>
                        <Icon name="chevron" />
                      </Link>
                    </div>
                    <div className="av-form-field">
                      <span>{t('contracts.fields.project')}</span>
                      {detail.contract.projectId ? (
                        <Link className="av-inline-link av-inline-link--field" to={`/projects/${detail.contract.projectId}`}>
                          <span>{model.projectName}</span>
                          <Icon name="chevron" />
                        </Link>
                      ) : (
                        <div className="av-readonly-text-block">{t('common.notSet')}</div>
                      )}
                    </div>
                  </div>
                </EditorSectionCard>

                <EditorSectionCard title={t('contracts.sections.parties')} actions={<OverflowMenuButton />}>
                  <div className="av-customer-secondary-grid">
                    <div className="av-summary-stack">
                      <strong>{t('contracts.parties.issuer')}</strong>
                      <span>{detail.contract.issuerSnapshot.legalName}</span>
                      <span>{detail.contract.issuerSnapshot.cui ?? t('common.notSet')}</span>
                      <span>{detail.contract.issuerSnapshot.registrationNumber ?? t('common.notSet')}</span>
                      <span>{formatSnapshotAddress(detail.contract.issuerSnapshot, t('common.notSet'))}</span>
                      <span>{formatSnapshotRepresentative(detail.contract.issuerSnapshot, t('common.notSet'))}</span>
                    </div>
                    <div className="av-summary-stack">
                      <strong>{t('contracts.parties.customer')}</strong>
                      <span>{detail.contract.customerSnapshot.legalName}</span>
                      <span>{detail.contract.customerSnapshot.cui ?? t('common.notSet')}</span>
                      <span>{detail.contract.customerSnapshot.registrationNumber ?? t('common.notSet')}</span>
                      <span>{formatSnapshotAddress(detail.contract.customerSnapshot, t('common.notSet'))}</span>
                      <span>{formatSnapshotRepresentative(detail.contract.customerSnapshot, t('common.notSet'))}</span>
                    </div>
                  </div>
                </EditorSectionCard>

                <EditorSectionCard title={t('contracts.sections.terms')} actions={<OverflowMenuButton />}>
                  <div className="av-form-grid">
                    <label className="av-form-field">
                      <span>{t('contracts.fields.effectiveFrom')}</span>
                      <TextField
                        type="date"
                        value={draft?.effectiveFrom ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, effectiveFrom: value } : current))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('contracts.fields.effectiveUntil')}</span>
                      <TextField
                        type="date"
                        value={draft?.effectiveUntil ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, effectiveUntil: value } : current))}
                      />
                    </label>
                  </div>
                  <div className="av-form-field">
                    <span>{t('contracts.fields.scope')}</span>
                    <TextAreaField
                      rows={4}
                      value={draft?.serviceScopeSummary ?? ''}
                      onValueChange={(value) => setDraft((current) => (current ? { ...current, serviceScopeSummary: value } : current))}
                    />
                  </div>
                  <div className="av-customer-secondary-grid">
                    <div className="av-form-field">
                      <span>{t('contracts.fields.renewalNotes')}</span>
                      <TextAreaField
                        rows={3}
                        value={draft?.renewalNotes ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, renewalNotes: value } : current))}
                      />
                    </div>
                    <div className="av-form-field">
                      <span>{t('contracts.fields.terminationNotes')}</span>
                      <TextAreaField
                        rows={3}
                        value={draft?.terminationNotes ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, terminationNotes: value } : current))}
                      />
                    </div>
                  </div>
                </EditorSectionCard>

                <EditorSectionCard title={t('contracts.sections.financials')} actions={<OverflowMenuButton />}>
                  <div className="av-form-grid">
                    <label className="av-form-field">
                      <span>{t('contracts.fields.value')}</span>
                      <TextField
                        type="number"
                        min={0}
                        step="0.01"
                        value={draft?.totalValue ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, totalValue: value } : current))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('contracts.fields.currency')}</span>
                      <TextField
                        value={draft?.currency ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, currency: value } : current))}
                      />
                    </label>
                  </div>
                  <div className="av-customer-secondary-grid">
                    <div className="av-form-field">
                      <span>{t('contracts.fields.paymentNotes')}</span>
                      <TextAreaField
                        rows={3}
                        value={draft?.paymentNotes ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, paymentNotes: value } : current))}
                      />
                    </div>
                    <div className="av-form-field">
                      <span>{t('contracts.fields.scheduleSummary')}</span>
                      <TextAreaField
                        rows={3}
                        value={draft?.scheduleSummary ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, scheduleSummary: value } : current))}
                      />
                    </div>
                  </div>
                </EditorSectionCard>
              </>
            ) : null}

            {editorTab === 'document' ? (
              <EditorSectionCard title={t('contracts.sections.document')} actions={<OverflowMenuButton />}>
                <div className="av-summary-stack">
                  <ValuePair label={t('contracts.document.templateVersion')} value={model.templateLabel} />
                  <ValuePair label={t('contracts.document.generatedAt')} value={model.generatedLabel} />
                  <ValuePair label={t('contracts.document.signatureStatus')} value={model.signatureLabel} />
                  <ValuePair label={t('contracts.document.sourceFormat')} value={detail.contract.generatedDocument?.sourceFormat ?? t('common.notSet')} />
                  <ValuePair label={t('contracts.document.pdfAsset')} value={detail.contract.pdfAssetId ?? t('common.notSet')} />
                  <div className="av-form-field">
                    <span>{t('contracts.document.resolvedBody')}</span>
                    <div className="av-readonly-text-block">{detail.contract.generatedDocument?.resolvedBodySource ?? t('common.notSet')}</div>
                  </div>
                </div>
              </EditorSectionCard>
            ) : null}

            {editorTab === 'activity' ? (
              <EditorSectionCard title={t('contracts.sections.activity')} actions={<OverflowMenuButton />}>
                {detail.activity.length === 0 ? (
                  <EmptyState title={t('contracts.sections.activity')} description={t('contracts.empty.activity')} />
                ) : (
                  <ul className="av-compact-list">
                    {detail.activity.map((item) => (
                      <li key={item.id} className="av-compact-list__item">
                        <div>
                          <strong>{item.label}</strong>
                          <span>{new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(item.occurredAt))}</span>
                        </div>
                        <StatusBadge label={t(`contracts.activityKinds.${item.kind}`)} tone="neutral" />
                      </li>
                    ))}
                  </ul>
                )}
              </EditorSectionCard>
            ) : null}
          </>
        }
        rail={
          <EditorSectionCard title={t('contracts.sections.documentSummary')} actions={<OverflowMenuButton />}>
            <div className="av-summary-stack">
              <ValuePair label={t('contracts.summary.issuer')} value={model.issuerName} />
              <ValuePair label={t('contracts.summary.customer')} value={model.customerName} />
              <ValuePair label={t('contracts.summary.generated')} value={model.generatedLabel} />
              <ValuePair label={t('contracts.summary.signature')} value={model.signatureLabel} />
            </div>
          </EditorSectionCard>
        }
      />
    </EditorPageLayout>
  );
}
